import React, { useRef, useState } from "react";
import { Camera, Image as ImageIcon, X, Loader2, Star } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// value: array of url strings. onChange(newArray). First item = cover.
export function PhotoUploader({ value = [], onChange, uploadPath = "/admin/upload" }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(uploadPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (e) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    onChange([...value, ...uploaded]);
    setUploading(false);
    if (uploaded.length) toast.success(`${uploaded.length} photo(s) uploaded`);
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const setCover = (i) => {
    const arr = [...value];
    const [item] = arr.splice(i, 1);
    onChange([item, ...arr]);
  };

  return (
    <div>
      <div className="flex gap-2">
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="camera-input" />
        <input ref={galleryRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="gallery-input" />
        <Button type="button" variant="outline" onClick={() => cameraRef.current?.click()} className="rounded-xl gap-2 flex-1 h-12" data-testid="camera-btn"><Camera className="w-4 h-4" /> Camera</Button>
        <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} className="rounded-xl gap-2 flex-1 h-12" data-testid="gallery-btn"><ImageIcon className="w-4 h-4" /> Gallery</Button>
      </div>
      {uploading && <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</p>}
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {value.map((url, i) => (
            <div key={`${url}-${i}`} className="relative rounded-xl overflow-hidden border border-border aspect-square group">
              <img src={resolveImg(url)} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute bottom-1 left-1 rounded-full bg-primary text-white text-[10px] font-bold px-1.5 py-0.5">Cover</span>}
              <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`remove-photo-${i}`}><X className="w-3.5 h-3.5" /></button>
              {i !== 0 && <button type="button" onClick={() => setCover(i)} className="absolute top-1 left-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center" title="Set as cover"><Star className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-2">JPG, JPEG, PNG, WebP. First photo is the cover. Tap ★ to change cover.</p>
    </div>
  );
}
